import type { Filter, Query, ResultSet } from '@cubejs-client/core'

import { toNumber } from '@/lib/cube-report'
import {
  KANTAR_VIEW,
  RECONCILE_DEPOTS,
  type AktarimRow,
  type DiaRow,
  type KantarRow,
} from '@/lib/kantar-dia'
import { buildCubeQuery } from '@/lib/cube'

/**
 * Mutabakat sorguları: ham satırlar (istemci eşleştirir). Kantar ile DİA farklı
 * sunucularda olduğu için Cube'de birleştirilemez (bkz. rpr183.yml).
 *
 * Cube tek sorguda en çok `ROW_LIMIT` satır döndürür; dolarsa sonraki sayfalar `offset`
 * ile çekilir (`useCubeLiveQuery({ paginate })`), toplam `MAX_ROWS`'ta durur ve ekran uyarır.
 * Sayfalar arası kararlı sıra için her sorgu satır anahtarına göre sıralanır.
 */
export const ROW_LIMIT = 50_000
export const MAX_ROWS = 200_000

const KANTAR_MEMBERS = [
  'kantarAdi',
  'tartino',
  'surecDurumu',
  'tarih',
  'giris',
  'cikis',
  'girisKg',
  'cikisKg',
  'cariKod',
  'cariAdi',
  'stokKod',
  'plaka',
  'sofor',
  'nakliyeci',
  'siparisNo',
] as const satisfies readonly (keyof KantarRow)[]

const DIA_MEMBERS = [
  'diaKalemKey',
  'kantarId',
  'tartiNo',
  'tartiOnek',
  'firmaKodu',
  'tarih',
  'irsaliyeNo',
  'irsaliyeTuru',
  'kalemTuru',
  'cariKod',
  'cariUnvan',
  'plaka',
  'surucu',
  'nakliyeciUnvan',
  'siparisBelgeNo',
  'stokKod',
  'miktar',
] as const satisfies readonly (keyof DiaRow)[]

export const AKTARIM_VIEW = 'IrsaliyeAktarim'

const AKTARIM_MEMBERS = [
  'kantarAdi',
  'tartino',
  'aktarildi',
  'aktarimHatasi',
] as const satisfies readonly (keyof AktarimRow)[]

const NUMERIC = new Set<string>([
  'tartino',
  'girisKg',
  'cikisKg',
  'tartiNo',
  'firmaKodu',
  'miktar',
  'aktarildi',
  'diaKalemKey',
])

/** Satır anahtarı: kantarda (kantar, tartı no) tekil; DİA'da kalem anahtarı. */
const ROW_ORDER: Record<string, string[]> = {
  [KANTAR_VIEW]: ['kantarAdi', 'tartino'],
  [AKTARIM_VIEW]: ['aktarimKey'],
}
const DIA_ORDER = ['diaKalemKey']

function rawQuery(view: string, members: readonly string[], filters: Filter[]): Query {
  return buildCubeQuery({
    dimensions: members.map((member) => `${view}.${member}`),
    filters,
    order: (ROW_ORDER[view] ?? DIA_ORDER).map((member): [string, 'asc'] => [
      `${view}.${member}`,
      'asc',
    ]),
    limit: ROW_LIMIT,
    ungrouped: true,
  })
}

function dateFilters(member: string, from?: string, to?: string): Filter[] {
  return [
    ...(from ? [{ member, operator: 'afterOrOnDate' as const, values: [from] }] : []),
    ...(to ? [{ member, operator: 'beforeOrOnDate' as const, values: [to] }] : []),
  ]
}

/** Aralıktaki tartımlar + tarihten bağımsız tüm Sahada kayıtları. */
export function kantarQuery(from?: string, to?: string): Query {
  const range = dateFilters(`${KANTAR_VIEW}.tarih`, from, to)
  const onsite: Filter = {
    member: `${KANTAR_VIEW}.surecDurumu`,
    operator: 'equals',
    values: ['Sahada'],
  }
  return rawQuery(
    KANTAR_VIEW,
    KANTAR_MEMBERS,
    range.length ? [{ or: [{ and: range }, onsite] }] : [],
  )
}

/** Aralık dışındaki eşleşen tartımlar (geç kesilen irsaliye): kantar -> tartı no'lar. */
export function kantarLookupQuery(keys: Map<string, number[]>): Query {
  const parts: Filter[] = [...keys].map(([kantar, tartis]) => ({
    and: [
      { member: `${KANTAR_VIEW}.kantarAdi`, operator: 'equals', values: [kantar] },
      { member: `${KANTAR_VIEW}.tartino`, operator: 'equals', values: tartis.map(String) },
    ],
  }))
  return rawQuery(KANTAR_VIEW, KANTAR_MEMBERS, [{ or: parts }])
}

/**
 * Sahada araçların aynı kantardaki sonraki çıkışlı tartıları (`reweighCandidates`: kantar ->
 * plakalar + en erken giriş günü). Yalnız 24 saatten genç Sahada kayıtları aranır; sonuç küçük.
 */
export function reweighQuery(candidates: Map<string, { plates: string[]; from: string }>): Query {
  const parts: Filter[] = [...candidates].map(([kantar, { plates, from }]) => ({
    and: [
      { member: `${KANTAR_VIEW}.kantarAdi`, operator: 'equals', values: [kantar] },
      { member: `${KANTAR_VIEW}.plaka`, operator: 'equals', values: plates },
      { member: `${KANTAR_VIEW}.giris`, operator: 'afterOrOnDate', values: [from] },
    ],
  }))
  return rawQuery(KANTAR_VIEW, KANTAR_MEMBERS, [
    { member: `${KANTAR_VIEW}.cikis`, operator: 'set' },
    { or: parts },
  ])
}

const depoFilter = (view: string): Filter => ({
  member: `${view}.depo`,
  operator: 'equals',
  values: [...RECONCILE_DEPOTS],
})

/**
 * `from`'dan (çağıran toleransı düşer, bkz. `diaFromDay`) DİA satırları; yalnız mutabakattaki
 * depolar (RECONCILE_DEPOTS). `to` yoksa bugüne kadar (aralıktaki tartılara sonradan
 * kesilenler dahil). Geçmiş aralıkta `to` verilir, geç satırlar `diaLateQuery` ile gelir.
 */
export function diaQuery(view: string, from?: string, to?: string): Query {
  return rawQuery(view, DIA_MEMBERS, [depoFilter(view), ...dateFilters(`${view}.tarih`, from, to)])
}

/**
 * `after` gününden sonraki DİA satırlarından kapsamdaki tartılara ait olabilecekler: kantar
 * başına tartı no parçaları (`lateTartiRuns`), ayrıca tartı no'su ya da kantarı boş satırlar
 * (ekranda kantar başına tek kayıtta toplanırlar). Toplu sorguyla kesişmez.
 */
export function diaLateQuery(
  view: string,
  after: string,
  runs: Map<string, [number, number][]>,
): Query {
  const parts: Filter[] = [
    ...[...runs].map(([kantar, list]) => ({
      and: [
        { member: `${view}.kantarId`, operator: 'equals' as const, values: [kantar] },
        {
          or: list.map(([min, max]) => ({
            and: [
              { member: `${view}.tartiNo`, operator: 'gte' as const, values: [String(min)] },
              { member: `${view}.tartiNo`, operator: 'lte' as const, values: [String(max)] },
            ],
          })),
        },
      ],
    })),
    { member: `${view}.tartiNo`, operator: 'notSet' },
    { member: `${view}.kantarId`, operator: 'notSet' },
  ]
  return rawQuery(view, DIA_MEMBERS, [
    depoFilter(view),
    { member: `${view}.tarih`, operator: 'afterDate', values: [after] },
    { or: parts },
  ])
}

/** Kantarın DİA aktarım sonuçları, yalnız verilen tartılar (`aktarimCandidates`). */
export function aktarimQuery(keys: Map<string, number[]>): Query {
  const parts: Filter[] = [...keys].map(([kantar, tartis]) => ({
    and: [
      { member: `${AKTARIM_VIEW}.kantarAdi`, operator: 'equals', values: [kantar] },
      { member: `${AKTARIM_VIEW}.tartino`, operator: 'equals', values: tartis.map(String) },
    ],
  }))
  return rawQuery(AKTARIM_VIEW, AKTARIM_MEMBERS, [{ or: parts }])
}

function mapRows<T>(view: string, members: readonly string[]) {
  return (result: ResultSet): T[] =>
    (result.rawData() as Record<string, unknown>[]).map(
      (row) =>
        Object.fromEntries(
          members.map((member) => {
            const value = row[`${view}.${member}`] ?? null
            return [member, NUMERIC.has(member) ? toNumber(value) : value]
          }),
        ) as T,
    )
}

export const toKantarRows = mapRows<KantarRow>(KANTAR_VIEW, KANTAR_MEMBERS)
export const toDiaRows = (view: string) => mapRows<DiaRow>(view, DIA_MEMBERS)
export const toAktarimRows = mapRows<AktarimRow>(AKTARIM_VIEW, AKTARIM_MEMBERS)
